import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const CONFIDENCE_TIERS = {
  high: {
    min: 0.8,
    label: "High",
    text: "text-emerald-600",
    dot: "bg-emerald-500",
  },
  medium: {
    min: 0.5,
    label: "Medium",
    text: "text-amber-600",
    dot: "bg-amber-500",
  },
  low: {
    min: 0,
    label: "Low",
    text: "text-rose-600",
    dot: "bg-rose-500",
  },
} as const;

/**
 * Resolves a 0–1 confidence score to its display tier.
 * @param score - Confidence score between 0 and 1
 * @returns The matching tier configuration
 */
function getConfidenceTier(score: number) {
  if (score >= CONFIDENCE_TIERS.high.min) {
    return CONFIDENCE_TIERS.high;
  }
  if (score >= CONFIDENCE_TIERS.medium.min) {
    return CONFIDENCE_TIERS.medium;
  }
  return CONFIDENCE_TIERS.low;
}

interface ConfidenceBadgeProps {
  /** Confidence score between 0 and 1 */
  score: number;
}

/**
 * Displays an AI confidence score as a color-coded badge with tooltip.
 * @param props.score - Confidence score between 0 and 1
 */
export function ConfidenceBadge({ score }: ConfidenceBadgeProps) {
  const tier = getConfidenceTier(score);
  const pct = Math.round(score * 100);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={`${tier.label} confidence: ${pct}%`}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 text-xs font-medium tabular-nums leading-none",
              tier.text,
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", tier.dot)} />
            {pct}%
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tier.label} confidence — AI certainty for this line item</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
